import React from "react";
import Navigation from "../Navigation";
import Header from "../Header";
import Footer from "../Footer";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";

const Privacy = () => (
  <div>
    <Navigation />
    <Header>
      <div className="heading text-uppercase">Privacy Policy</div>
    </Header>
    <section className="page-section" id="privacy">
      <Container>
        <Row>
          <Col lg="12" className="text-left legal-content">
            <h2 className="section-heading text-uppercase text-center">Privacy Policy</h2>
            <p className="text-muted text-center">
              <strong>Qvirt Labs Inc.</strong> — Effective Date: February 8, 2026 — Last Updated: February 8, 2026
            </p>

            <h3>1. Introduction</h3>
            <p>
              Qvirt Labs Inc. ("Qvirt," "we," "us," or "our") operates the QvirtAdmin application and related services (collectively, the "Service"). This Privacy Policy describes how we collect, use, disclose, retain, and protect your personal information when you use our Service, including our integration with Intuit QuickBooks Online.
            </p>
            <p>
              This policy complies with the British Columbia <em>Personal Information Protection Act</em> (PIPA), the <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA), and Intuit's platform policies.
            </p>

            <h3>2. Information We Collect</h3>

            <h4>2.1 Information You Provide</h4>
            <ul>
              <li><strong>Account information:</strong> Name, email address, business name, and contact details you provide when using the Service.</li>
              <li><strong>QuickBooks Online data:</strong> When you connect your QuickBooks Online account, we access financial data including customer records, invoices, items, accounts, vendors, and related transaction data as authorized by you through Intuit's OAuth process.</li>
              <li><strong>Google Sheets data:</strong> When you connect Google Sheets, we access spreadsheet data you designate for use with the Service.</li>
            </ul>

            <h4>2.2 Information Collected Automatically</h4>
            <ul>
              <li><strong>Usage data:</strong> We may collect information about how you interact with the Service, including access times and features used.</li>
              <li><strong>Technical data:</strong> Device type, operating system, and browser type.</li>
            </ul>

            <h4>2.3 Information We Do Not Collect</h4>
            <ul>
              <li>We do not collect financial account numbers, bank credentials, or payment card information directly.</li>
              <li>We do not collect personal information from children under 13.</li>
            </ul>

            <h3>3. How We Use Your Information</h3>
            <p>We use your information solely for the following purposes:</p>
            <ul>
              <li><strong>Providing the Service:</strong> Creating invoices, managing QuickBooks Online data, and performing administrative tasks you direct.</li>
              <li><strong>Maintaining your connection:</strong> Storing OAuth tokens to maintain your authorized connection to QuickBooks Online and Google Sheets.</li>
              <li><strong>Improving the Service:</strong> Understanding usage patterns to improve functionality and fix issues.</li>
              <li><strong>Legal compliance:</strong> Meeting legal obligations under applicable law.</li>
            </ul>
            <p>We do <strong>not</strong> use your data for advertising, marketing to third parties, or any purpose unrelated to providing the Service.</p>

            <h3>4. How We Share Your Information</h3>
            <p>We do <strong>not</strong> sell, rent, or trade your personal information.</p>
            <p>We may share your information only in the following circumstances:</p>
            <ul>
              <li><strong>With Intuit:</strong> We exchange data with QuickBooks Online through Intuit's APIs as necessary to provide the Service, subject to Intuit's privacy policy and your authorization.</li>
              <li><strong>With Google:</strong> We exchange data with Google Sheets through Google's APIs as necessary to provide the Service, subject to Google's privacy policy and your authorization.</li>
              <li><strong>Legal requirements:</strong> We may disclose information if required by law, regulation, legal process, or governmental request.</li>
              <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets, your information may be transferred, subject to the same privacy protections described in this policy.</li>
            </ul>

            <h3>5. Data Retention</h3>
            <ul>
              <li><strong>QuickBooks Online data:</strong> We access your QBO data in real time through Intuit's APIs. We do not maintain a separate persistent copy of your QuickBooks data. OAuth tokens are stored locally on your system for the duration of your authorized session.</li>
              <li><strong>Google Sheets data:</strong> We access your spreadsheet data in real time through Google's APIs. We do not maintain a separate persistent copy.</li>
              <li><strong>Account information:</strong> We retain your account information for as long as your account is active or as needed to provide the Service.</li>
              <li><strong>Upon termination:</strong> When you stop using the Service or revoke access, we delete any stored tokens and cease accessing your data. You may also revoke access directly through your Intuit or Google account settings at any time.</li>
            </ul>

            <h3>6. Data Security</h3>
            <p>We implement reasonable technical and organizational measures to protect your personal information, including:</p>
            <ul>
              <li>OAuth 2.0 for secure authentication with QuickBooks Online and Google.</li>
              <li>Encrypted storage of access and refresh tokens.</li>
              <li>Access limited to authorized personnel on a need-to-know basis.</li>
            </ul>
            <p>No method of transmission or storage is completely secure. While we strive to protect your information, we cannot guarantee absolute security.</p>

            <h3>7. Your Rights</h3>
            <p>Under BC PIPA and PIPEDA, you have the right to:</p>
            <ul>
              <li><strong>Access</strong> your personal information held by us.</li>
              <li><strong>Correct</strong> any inaccurate or incomplete personal information.</li>
              <li><strong>Withdraw consent</strong> for the collection, use, or disclosure of your personal information, subject to legal or contractual restrictions.</li>
              <li><strong>Request deletion</strong> of your personal information.</li>
              <li><strong>Revoke access</strong> to your QuickBooks Online or Google accounts at any time through those platforms' settings.</li>
            </ul>
            <p>To exercise any of these rights, contact us at derekja@qvirt.com. We will respond within 30 business days.</p>

            <h3>8. Third-Party Services</h3>
            <p>The Service integrates with third-party platforms (Intuit QuickBooks Online and Google Sheets). Your use of those platforms is governed by their respective privacy policies:</p>
            <ul>
              <li>Intuit Privacy Policy: <a href="https://www.intuit.com/privacy/" target="_blank" rel="noopener noreferrer">https://www.intuit.com/privacy/</a></li>
              <li>Google Privacy Policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></li>
            </ul>

            <h3>9. International Data Transfers</h3>
            <p>Your data is processed in Canada. If data is transferred outside of British Columbia or Canada, we ensure appropriate safeguards are in place consistent with PIPEDA and PIPA requirements.</p>

            <h3>10. Changes to This Policy</h3>
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website with a revised "Last Updated" date. Your continued use of the Service after changes constitutes acceptance of the updated policy.</p>

            <h3>11. Contact Us</h3>
            <p>If you have questions, concerns, or complaints about this Privacy Policy or our privacy practices, contact:</p>
            <p>
              <strong>Qvirt Labs Inc.</strong><br />
              Email: <a href="mailto:derekja@qvirt.com">derekja@qvirt.com</a><br />
              Website: <a href="https://qvirt.com" target="_blank" rel="noopener noreferrer">https://qvirt.com</a>
            </p>
          </Col>
        </Row>
      </Container>
    </section>
    <Footer />
  </div>
);

export default Privacy;
